import type { TemplatePortfolioDraft } from "@adpilot/shared";
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconCircleCheck,
  IconFileExcel,
  IconFileUpload,
  IconLoader2,
  IconRefresh,
  IconSparkles,
  IconUpload,
  IconX,
} from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { type ColumnMapping, columnLetter, type ImportField } from "./excel";
import type { PendingExcelFile, useExcelImport } from "./use-excel-import";

export interface ClassifyProgress {
  active: boolean;
  done: number;
  total: number;
  remainingMinutes: number;
  /** Keywords whose classification failed (0 = none). */
  failed: number;
}

interface ImportPanelProps {
  /** empty = TP-02a, files = TP-02 / TP-04, compact = TP-05 (everything classified). */
  variant: "empty" | "files" | "compact";
  draft: TemplatePortfolioDraft;
  keywordCount: number;
  allClassified: boolean;
  excel: ReturnType<typeof useExcelImport>;
  onPick: () => void;
  classify: ClassifyProgress;
  onClassify: () => void;
  onRetry: () => void;
  retrying: boolean;
}

const nf = (value: number) => value.toLocaleString("en-US");

export function ImportPanel(props: ImportPanelProps) {
  if (props.variant === "compact") return <CompactImportCard {...props} />;
  return (
    <div className="flex gap-4">
      <ExcelDropzone
        onPick={props.onPick}
        onFiles={props.excel.addFiles}
        busy={props.excel.busy}
        wide={props.variant === "empty"}
      />
      {props.variant === "files" ? <ImportedFilesCard {...props} /> : null}
    </div>
  );
}

function ExcelDropzone({
  onPick,
  onFiles,
  busy,
  wide,
}: {
  onPick: () => void;
  onFiles: (files: File[]) => void;
  busy: boolean;
  wide: boolean;
}) {
  const { t } = useTranslation("templatePortfolio");
  const [over, setOver] = useState(false);
  return (
    <button
      type="button"
      disabled={busy}
      onClick={onPick}
      onDragOver={(event) => {
        event.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(event) => {
        event.preventDefault();
        setOver(false);
        const files = [...event.dataTransfer.files].filter((file) => /\.xlsx$/i.test(file.name));
        if (files.length > 0) onFiles(files);
      }}
      className={cn(
        "flex shrink-0 cursor-pointer flex-col items-center justify-center gap-2.5 rounded-[14px] border-[1.5px] border-dashed border-control-border-hover bg-canvas p-6 outline-none transition-colors hover:bg-subtle focus-visible:ring-2 focus-visible:ring-ring/15 disabled:cursor-progress",
        wide ? "h-[200px] w-full" : "w-[340px]",
        over && "border-primary bg-subtle",
      )}
    >
      <span className="flex size-10 items-center justify-center rounded-[10px] border border-line bg-surface">
        {busy ? (
          <IconLoader2 className="size-5 animate-spin text-ink" aria-hidden />
        ) : (
          <IconFileUpload className="size-5 text-ink" aria-hidden />
        )}
      </span>
      <span className="text-center text-[13px] font-semibold leading-[18px] text-ink">
        {t("keywords.import.dropTitle")}
      </span>
      <span className="text-center text-xs leading-[18px] text-muted-foreground">
        {t("keywords.import.dropHint")}
      </span>
    </button>
  );
}

function ImportedFilesCard({
  draft,
  keywordCount,
  excel,
  classify,
  onClassify,
  onRetry,
  retrying,
}: ImportPanelProps) {
  const { t } = useTranslation("templatePortfolio");
  const showProgress = classify.active || classify.failed > 0;
  return (
    <div className="flex min-w-0 flex-1 flex-col overflow-clip rounded-[14px] border border-line bg-surface">
      {draft.importedFiles.map((file) => (
        <div
          key={`${file.fileName}-${file.importedAt}`}
          className="flex items-center gap-3 border-b border-line px-4 py-3"
        >
          <IconCircleCheck className="size-5 shrink-0 text-success-foreground" aria-hidden />
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="truncate text-[13px] font-medium leading-[18px] text-ink">
              {file.fileName}
            </span>
            <span className="text-xs text-muted-foreground">
              {t("keywords.import.fileRows", { count: file.rowCount, value: nf(file.rowCount) })}
            </span>
          </div>
        </div>
      ))}
      {excel.pending.map((file) => (
        <ColumnMappingRow
          key={file.id}
          file={file}
          busy={excel.busy}
          onChange={(mapping) => excel.setMapping(file.id, mapping)}
          onDismiss={() => excel.dismiss(file.id)}
          onApply={() => excel.applyMapping(file)}
        />
      ))}
      {showProgress ? (
        <div className="flex flex-1 flex-col gap-3 bg-surface px-4 py-3.5">
          {classify.active ? (
            <>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <IconLoader2 className="size-4 animate-spin text-primary" aria-hidden />
                  <span className="text-[13px] font-semibold leading-[18px] text-ink">
                    {t("keywords.classify.progress", {
                      done: nf(classify.done),
                      total: nf(classify.total),
                    })}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">
                  {t("keywords.classify.remaining", { count: classify.remainingMinutes })}
                </span>
              </div>
              <div className="flex h-1.5 shrink-0 overflow-clip rounded-full bg-gray-200">
                <div
                  className="h-1.5 rounded-full bg-primary transition-[width]"
                  style={{
                    width: `${classify.total > 0 ? Math.round((classify.done / classify.total) * 100) : 0}%`,
                  }}
                />
              </div>
            </>
          ) : null}
          {classify.failed > 0 ? (
            <div className="flex items-center justify-between gap-3 rounded-[10px] border border-badge-destructive-border bg-badge-destructive-background px-3 py-2.5">
              <div className="flex min-w-0 items-center gap-2">
                <IconAlertCircle
                  className="size-4 shrink-0 text-badge-destructive-foreground"
                  aria-hidden
                />
                <span className="text-xs text-badge-destructive-foreground">
                  {t("keywords.classify.failed", { value: nf(classify.failed) })}
                </span>
              </div>
              <Button
                variant="outline"
                size="xs"
                loading={retrying}
                disabled={classify.active}
                onClick={onRetry}
                className="h-7 shrink-0 rounded-md border-badge-destructive-border bg-surface px-2.5 text-xs text-badge-destructive-foreground"
              >
                <IconRefresh className="size-3.5" aria-hidden />
                {t("keywords.classify.retry")}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
      {classify.active ? null : (
        <div className="mt-auto flex items-center justify-between gap-4 border-t border-line bg-table-footer px-4 py-3">
          <span className="text-[13px] font-semibold leading-[18px] text-ink">
            {t("keywords.import.imported", { count: keywordCount, value: nf(keywordCount) })}
          </span>
          {classify.failed > 0 ? null : (
            <Button size="sm" className="h-8 rounded-lg px-3 text-xs" onClick={onClassify}>
              <IconSparkles className="size-3.5" aria-hidden />
              {t("keywords.classify.action")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

const MAPPED_FIELDS = [
  { field: "keyword", label: "keywords.import.keywordColumn", width: "w-[220px]", required: true },
  {
    field: "monthlySearchVolume",
    label: "keywords.import.searchVolumeColumn",
    width: "w-[180px]",
    required: false,
  },
  {
    field: "monthlyPurchases",
    label: "keywords.import.purchasesColumn",
    width: "w-[180px]",
    required: false,
  },
] as const satisfies readonly {
  field: ImportField;
  label: string;
  width: string;
  required: boolean;
}[];

function ColumnMappingRow({
  file,
  busy,
  onChange,
  onDismiss,
  onApply,
}: {
  file: PendingExcelFile;
  busy: boolean;
  onChange: (mapping: ColumnMapping) => void;
  onDismiss: () => void;
  onApply: () => void;
}) {
  const { t } = useTranslation("templatePortfolio");
  const columns = file.sheet.headers.map((header, index) => ({
    value: String(index),
    label: t("keywords.import.column", { letter: columnLetter(index), header }),
  }));
  return (
    <div className="flex flex-col gap-3 border-b border-line bg-amber-2 px-4 py-3">
      <div className="flex items-center gap-3">
        <IconAlertTriangle className="size-5 shrink-0 text-warning-foreground" aria-hidden />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="truncate text-[13px] font-medium leading-[18px] text-ink">
            {file.fileName}
          </span>
          <span className="text-xs text-warning-foreground">{t("keywords.import.unmapped")}</span>
        </div>
        <Button
          variant="ghost"
          size="icon-xs"
          aria-label={t("keywords.import.removeFile")}
          onClick={onDismiss}
        >
          <IconX aria-hidden />
        </Button>
      </div>
      <div className="flex items-end gap-3 pl-8">
        {MAPPED_FIELDS.map(({ field, label, width, required }) => {
          const items = required
            ? columns
            : [{ value: null, label: t("keywords.import.notMapped") }, ...columns];
          const current = file.mapping[field];
          return (
            <div key={field} className={cn("flex shrink-0 flex-col gap-1.5", width)}>
              <span
                className={cn("text-xs", required ? "font-medium text-ink" : "text-text-secondary")}
              >
                {t(label)}
              </span>
              <Select
                items={items}
                value={current === undefined ? null : String(current)}
                onValueChange={(value) => {
                  const next = { ...file.mapping };
                  if (value === null) delete next[field];
                  else next[field] = Number(value);
                  onChange(next);
                }}
              >
                <SelectTrigger
                  size="sm"
                  className="h-8 w-full rounded-lg bg-surface px-2.5 text-xs"
                >
                  <SelectValue
                    placeholder={t("keywords.import.notMapped")}
                    className="truncate data-placeholder:text-placeholder"
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {items.map((item) => (
                      <SelectItem key={item.value ?? "none"} value={item.value}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          );
        })}
        <Button
          size="sm"
          disabled={file.mapping.keyword === undefined}
          loading={busy}
          onClick={onApply}
          className="h-8 rounded-lg border-ink bg-ink px-3 text-xs text-surface hover:border-ink hover:bg-ink/90"
        >
          {t("keywords.import.applyMapping")}
        </Button>
      </div>
    </div>
  );
}

function CompactImportCard({
  draft,
  keywordCount,
  allClassified,
  excel,
  onPick,
  onClassify,
}: ImportPanelProps) {
  const { t } = useTranslation("templatePortfolio");
  const files = draft.importedFiles;
  const first = files[0]?.fileName;
  const status = [
    files.length > 1
      ? t("keywords.import.filesSummary", { count: files.length, name: first })
      : first,
    allClassified ? t("keywords.import.allClassified") : null,
  ].filter(Boolean);
  return (
    <div className="flex items-center justify-between gap-4 rounded-[14px] border border-line bg-surface px-4 py-3">
      <div className="flex min-w-0 items-center gap-3">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-badge-success-background">
          <IconFileExcel className="size-4 text-success-foreground" aria-hidden />
        </span>
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-[13px] font-semibold leading-[18px] text-ink">
            {t("keywords.import.imported", { count: keywordCount, value: nf(keywordCount) })}
          </span>
          {status.length > 0 ? (
            <span className="truncate text-xs text-muted-foreground">{status.join(" · ")}</span>
          ) : null}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          loading={excel.busy}
          onClick={onPick}
          className="h-8 rounded-lg border-line bg-surface px-3 text-xs text-ink"
        >
          <IconUpload className="size-3.5" aria-hidden />
          {t("keywords.import.continue")}
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={allClassified}
          onClick={onClassify}
          className="h-8 rounded-lg border-line bg-surface px-3 text-xs text-ink disabled:opacity-40"
        >
          <IconSparkles className="size-3.5" aria-hidden />
          {t("keywords.classify.action")}
        </Button>
      </div>
    </div>
  );
}
